import React, { useState, useEffect } from "react";
import api from "./api";
import "./ColostrumPage.css";

const BREEDS = ["Holstein", "Jersey", "Crossbred", "Colored Breed"];

const DEFAULTS = {
  breed: "Holstein",
  hours_old: "2",
  volume_qt: "4",
  brix_pct: "22",
};

export default function ColostrumPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fire-and-forget analytics beacon on first load. Failures are ignored so
  // they never affect the calculator.
  useEffect(() => {
    api
      .post("/analytics/collect", {
        path: window.location.pathname,
        referrer: document.referrer || null,
      })
      .catch(() => {});
  }, []);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload = {
        breed: form.breed,
        hours_old: parseFloat(String(form.hours_old).replace(",", ".")),
        volume_qt: parseFloat(String(form.volume_qt).replace(",", ".")),
        brix_pct: parseFloat(String(form.brix_pct).replace(",", ".")),
      };
      const res = await api.post("/colostrum-calc/calculate", payload);
      setResult(res.data);
      // The Herald's Hall beacon (public/index.html). Optional: if the script
      // is blocked or not loaded yet, the calculator is unaffected.
      try {
        window.herald?.event?.("calc", {
          breed: payload.breed,
          hours_old: payload.hours_old,
          volume_qt: payload.volume_qt,
          brix_pct: payload.brix_pct,
          verdict: res.data.verdict,
          packets_needed: res.data.packets_needed,
          amount_of_supplement_g: res.data.amount_of_supplement_g,
        });
      } catch {}
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Could not calculate. Check the inputs and try again."
      );
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(DEFAULTS);
    setResult(null);
    setError(null);
  };

  const adequate = result?.verdict === "adequate";

  return (
    <div
      className="colostrum-shell"
      style={{ "--cow-bg": `url(${process.env.PUBLIC_URL}/pasture-bg.jpg)` }}
    >
      <div className="colostrum-grid">
        <header className="colostrum-header">
          <a
            className="brand-logo-link"
            href="https://biggain.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Big Gain — visit biggain.com"
          >
            <img className="brand-logo" src="/biggain-logo.png" alt="Big Gain" />
          </a>
          <p className="eyebrow">Dairy Calf Tools</p>
          <h1>
            Colostrum <span className="accent">supplement</span> calculator
          </h1>
          <p className="lede">
            Estimate the IgG a calf absorbs and the replacer needed to close any
            gap.
          </p>
        </header>

        <section className="panel form-panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Inputs</p>
              <h3>Feed details</h3>
            </div>
          </div>

          <form className="colostrum-form" onSubmit={submit}>
            <label>
              <span>Breed</span>
              <select value={form.breed} onChange={update("breed")} required>
                {BREEDS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Hours since birth</span>
              <input
                type="number"
                min="1"
                max="24"
                step="0.5"
                value={form.hours_old}
                onChange={update("hours_old")}
                required
              />
            </label>

            <label>
              <span>Colostrum volume (qt)</span>
              <input
                type="number"
                min="0.1"
                max="8"
                step="0.1"
                value={form.volume_qt}
                onChange={update("volume_qt")}
                required
              />
            </label>

            <label>
              <span>Brix reading (%)</span>
              <input
                type="number"
                min="10"
                max="35"
                step="0.1"
                value={form.brix_pct}
                onChange={update("brix_pct")}
                required
              />
            </label>

            <div className="form-actions">
              <button type="button" className="text-btn" onClick={reset}>
                Reset
              </button>
              <button type="submit" className="primary-btn" disabled={loading}>
                {loading ? "Calculating…" : "Calculate"}
              </button>
            </div>
          </form>

          {error && <p className="error-msg">{error}</p>}
        </section>

        <section className="panel result-panel">
          <div className="panel-header">
            <div>
              <p className="panel-kicker">Result</p>
            </div>
          </div>

          {!result && (
            <p className="empty">
              Fill in the form and press Calculate to see the recommendation.
            </p>
          )}

          {result && (
            <>
              <div
                className={`verdict-card ${
                  adequate ? "verdict-ok" : "verdict-supplement"
                }`}
              >
                <p className="verdict-label">
                  {adequate ? "Adequate" : "Supplement needed"}
                </p>
                {!adequate && (
                  <>
                    <p className="verdict-amount">
                      {result.amount_of_supplement_g} g
                      <span className="verdict-amount-unit"> of replacer</span>
                    </p>
                    <p className="verdict-sub">
                      Closes a {result.igg_from_supplement_needed_g} g IgG gap
                      {result.packets_needed > 0 && (
                        <>
                          {" "}
                          · ≈ {result.packets_needed} packet
                          {result.packets_needed === 1 ? "" : "s"}
                        </>
                      )}
                    </p>
                  </>
                )}
                {adequate && (
                  <p className="verdict-sub">
                    Maternal colostrum already meets the breed target.
                  </p>
                )}
              </div>

              {result.notices?.length > 0 && (
                <ul className="notices">
                  {result.notices.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}

              <div className="breakdown">
                <p className="breakdown-title">Breakdown</p>
                <dl>
                  <div>
                    <dt>Colostrum IgG concentration</dt>
                    <dd>{result.colostrum_concentration_g_per_L} g/L</dd>
                  </div>
                  <div>
                    <dt>Maternal absorption efficiency</dt>
                    <dd>{Math.round(result.maternal_aea * 100)}%</dd>
                  </div>
                  <div>
                    <dt>Total IgG in feed</dt>
                    <dd>{result.colostrum_supply_g} g</dd>
                  </div>
                  <div>
                    <dt>IgG absorbed from maternal feed</dt>
                    <dd>{result.apparent_absorbed_g} g</dd>
                  </div>
                  <div>
                    <dt>Breed target (absorbed)</dt>
                    <dd>{result.target_absorbed_g} g</dd>
                  </div>
                  {result.target_supply_g > 0 && (
                    <div>
                      <dt>Target feed to hit goal (ref.)</dt>
                      <dd>{result.target_supply_g} g</dd>
                    </div>
                  )}
                </dl>
              </div>
            </>
          )}
        </section>

        <footer className="colostrum-footer">
          <a
            className="footer-brand"
            href="https://biggain.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Big Gain
          </a>
          <span className="footer-tag">Helping Produce Food</span>
        </footer>
      </div>
    </div>
  );
}
